import { trpc2 } from "../../utils/trpc";
import MonacoEditorWrapper from "./MonacoEditorWrapper";
import { AutoCompleteData, MonacoType, ActionDescriptior, MonacoModel, MonacoRange, MonacoEditor as MonacoEditorType } from "./monaco.types";
import { ConnectionType } from "@prisma/client";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";

interface AvenueColumn {
  column_name: string;
  data_type: string;
}

interface AvenueTable {
  table_name: string;
  table_schema: string;
  columns: AvenueColumn[];
}

interface Props {
  defaultValue: string;
  formattedValue?: string;
  onChange: (value: string) => void;
  databaseFormName: string;
  runQuery?: () => void;
  formatQuery?: () => void;
  readOnly?: boolean;
  initialHeight?: number;
  didMountCallback?: (editor: MonacoEditorType) => void;
}

export default function SqlEditor({
  defaultValue,
  formattedValue,
  onChange,
  databaseFormName,
  runQuery,
  formatQuery,
  initialHeight,
  didMountCallback,
  readOnly = false,
}: Props): React.ReactElement {
  const [editor, setEditor] = React.useState<MonacoEditorType>();
  const { watch } = useFormContext();
  const databaseId = watch(databaseFormName);

  const { data: connectionData } = trpc2.v2.databaseConnections.getSchema.useQuery(
    { connectionId: databaseId },
    {
      enabled: !!databaseId,
    }
  );

  const getAutoCompleteData = useCallback(
    (model: MonacoModel, wordRange: MonacoRange, triggerCharacter?: string) => {
      const tables = connectionData?.tables;

      const capitalLetRegex = /^.*[A-Z].*$/;
      const newAutoCompleteData: AutoCompleteData[] = [];
      // If there is no connection data about the users database, exit
      if (!tables) {
        return newAutoCompleteData;
      }
      if (triggerCharacter === ".") {
        const positionCharBeforeWord: MonacoRange = {
          ...wordRange,
          startColumn: wordRange.startColumn - 2,
        };
        // Get the whole word that the user is typing as search text to check if there is an "
        const wordWithCharBefore = model.getValueInRange(positionCharBeforeWord);
        const tableWithQuote = wordWithCharBefore[0] === '"';
        // Get the position of the previous word (word before ".") which should be a table name
        const prevWordPos = {
          lineNumber: wordRange.endLineNumber,
          column: tableWithQuote ? wordRange.endColumn - 2 : wordRange.endColumn - 1,
        };
        // The table name, if there isnt a table name we dont want to autocomplete
        const prevWord = model.getWordUntilPosition(prevWordPos).word;
        if (!prevWord) {
          return newAutoCompleteData;
        }
        // Get the matching table and suggest autocomplete with its columns
        const table = tables.find((table) => table.table_name === prevWord);
        table?.columns.forEach((column) => {
          // Hide columns that are internal/system made
          if (column.column_name && column.column_name.at(0) !== "_") {
            const colInsertText = capitalLetRegex.test(column.column_name) ? `"${column.column_name}"` : column.column_name;
            newAutoCompleteData.push({
              label: {
                label: `${column.column_name}`,
                description: "Column",
                detail: ` ${column.data_type}`,
              },
              insertText: `${colInsertText}`,
              sortText: `${column.column_name}`,
              filterText: `${column.column_name}`,
              detail: column.data_type,
              documentation: `Column in Table ${table.table_name}`,
            });
          }
        });
        return newAutoCompleteData;
      }
      const positionCharBeforeWord: MonacoRange = {
        ...wordRange,
        startColumn: wordRange.startColumn - 1,
      };
      // Get the word the user is typing with the character before, if its " then we dont need to autocomplete quotes
      const wordWithCharBefore = model.getValueInRange(positionCharBeforeWord);
      const openQuote = wordWithCharBefore[0] === '"';
      tables
        .filter((table) => !table.table_schema || table.table_schema === "public")
        .forEach((table) => {
          const tableInsertText = !openQuote && capitalLetRegex.test(table.table_name) ? `"${table.table_name}"` : table.table_name;
          newAutoCompleteData.push({
            label: {
              label: table.table_name,
              detail: " Table",
            },
            insertText: tableInsertText,
            detail: "Table",
            sortText: `__${table.table_name}`,
            filterText: table.table_name,
            documentation: "Database Table",
          });
          table.columns.forEach((column: any) => {
            if (column.column_name.at(0) !== "_") {
              const colInsertText = !openQuote && capitalLetRegex.test(column.column_name) ? `"${column.column_name}"` : column.column_name;
              newAutoCompleteData.push({
                label: {
                  label: `${column.column_name}`,
                  description: `${table.table_name} Column`,
                  detail: ` ${column.data_type}`,
                },
                insertText: `${colInsertText}`,
                sortText: `_${column.column_name}`,
                filterText: `${column.column_name}`,
                detail: column.data_type,
                documentation: `Column in Table ${table.table_name}`,
              });
            }
          });
        });
      return newAutoCompleteData;
    },
    [connectionData]
  );

  const editorActions = (monaco: MonacoType) => {
    let actions = [];
    if (runQuery) {
      actions.push({
        id: "run_query",
        label: "Run Query",
        contextMenuOrder: 0,
        contextMenuGroupId: "query_actions",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
        run: runQuery,
      } as ActionDescriptior);
    }
    if (formatQuery && connectionData?.databaseType !== ConnectionType.MONGODB) {
      actions.push({
        id: "format_query",
        label: "Format Query",
        contextMenuOrder: 1,
        contextMenuGroupId: "query_actions",
        keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.Digit1],
        run: formatQuery,
      });
    }
    return actions;
  };

  let language: string;
  switch (connectionData?.databaseType) {
    case ConnectionType.AIRTABLE:
    case ConnectionType.MONGODB:
      language = "json";
      break;
    case ConnectionType.GOOGLE_SHEETS:
      language = "text";
      break;
    default:
      language = "sql";
      break;
  }

  return (
    <MonacoEditorWrapper
      language={language}
      defaultValue={defaultValue}
      formattedValue={formattedValue}
      onChange={onChange}
      getAutoCompleteData={getAutoCompleteData}
      actions={editorActions}
      readOnly={readOnly}
      initialHeight={initialHeight}
      didMountCallback={(editor) => {
        didMountCallback?.(editor);
        setEditor(editor);
      }}
    />
  );
}
